import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";

import { MatchDayService, SportEnum, User } from "../client/api";
import { fb } from "../config/firebase";
import { loggerSettings } from "../store/middleware/logger";
import { formatYmdLocal, isAdmin, isFiniteDate, toLocalMidnight } from "../config/utils";
import EventsSocket from "../client/Websockets/events";
import client from "../client";
import { pathHome, pathHomeDate, toYmd } from "../config/routes";
import { AS_AOI, AS_LANG } from "../config/constants";

function isValidDate(d: unknown): d is Date {
  return d instanceof Date && isFiniteDate(d);
}

type UseUserBootstrapParams = {
  user?: User | null;
  sports?: SportEnum | null;
  getLeagues: (aoi: number, sports: SportEnum) => void;
  resetStoredData: () => void;
};

const isInHomeArea = (pathname: string) =>
  pathname === "/" || pathname === "/home" || pathname.startsWith("/home/");

export const useUserBootstrap = ({ user, sports, getLeagues, resetStoredData }: UseUserBootstrapParams) => {
  const navigate = useNavigate();
  const location = useLocation();

  const aoiRef = useRef(user?.areaOfInterest.id ?? -1);
  const aoiFlowIdRef = useRef(0);

  useEffect(() => {
    if (!user) return;

    localStorage.setItem(AS_AOI, user.areaOfInterest.id.toString(10));
    localStorage.setItem(AS_LANG, user.language.id.toString(10));

    fb().subscribeUserSpecific(user);
    if (sports) {
      fb().tagUser(user, sports);
    }
    EventsSocket.setUser(user.id, user.areaOfInterest.id);
    client().setLang(user.language.locale);
    loggerSettings.enabled = isAdmin(user);
    if (sports) {
      getLeagues(user.areaOfInterest.regionId, sports);
    }

    if (aoiRef.current === -1 || aoiRef.current !== user.areaOfInterest.id) {
      aoiRef.current = user.areaOfInterest.id;
      resetStoredData();

      const flowId = ++aoiFlowIdRef.current;

      if (!sports) return;

      if (isInHomeArea(location.pathname)) {
        navigate(pathHome(), { replace: true });
      }

      (async () => {
        try {
          const dates =
            await MatchDayService.getMatchDatesTodayMatchDayTodayGet(
              user.areaOfInterest.id as number,
              undefined,
              undefined,
              sports
            );

          if (flowId !== aoiFlowIdRef.current) return;

          const today = toLocalMidnight(new Date());
          const normalized = (dates || [])
            .map((d) => toLocalMidnight(new Date(d)))
            .filter(isValidDate)
            .sort((a, b) => a.getTime() - b.getTime());

          let target: Date | undefined;
          const todayYmd = formatYmdLocal(today);
          const hasToday = normalized.some((d) => formatYmdLocal(d) === todayYmd);
          if (hasToday) target = today;
          else {
            const future = normalized.filter((d) => d.getTime() >= today.getTime());
            target = future[0] ?? normalized[normalized.length - 1];
          }


          if (target && flowId === aoiFlowIdRef.current && isInHomeArea(location.pathname)) {
            const ymd = toYmd(target);
            navigate(pathHomeDate(ymd), { replace: true });
          }
        } catch (_) {
          // nothing
        }
      })();
    }
  }, [
    getLeagues,
    resetStoredData,
    sports,
    user,
    navigate,
    location.pathname
  ]);
};

